// @file View-as preview URL helpers and persona display helpers for the preview banner.
import { __ } from '@@/bits/intl'
import { clearSearchParam, reload, setSearchParam } from '@@/bits/location'
import { LibraryType, WallAccessRole } from '@@/enums'

/**
 * One selectable audience on the wall, sent unclamped while previewing. `name` is null for
 * visitor and library_member (their words are localised on the client); `libraryType` is set
 * on library_member only. `role` is already filtered to the previewable set server side, so
 * `admin` never appears and `none` may. `key` is exactly the `view_as` URL value.
 */
interface ViewAsPreviewPersona {
  key: string
  name: string | null
  libraryType: string | null
  role: WallAccessRole
}

const PERSONA_PARAM = 'view_as'

function enterPreview(personaKey: string): void {
  setSearchParam(PERSONA_PARAM, personaKey, true)
  reload()
}

function exitPreview(): void {
  // `true` means replaceState here, so Back cannot restore the preview URL.
  clearSearchParam(PERSONA_PARAM, true)
  reload()
}

/**
 * Localised audience word for the visitor and library_member personas. Keyed on
 * `libraryType` (not the surface store) so the no-access page can reuse it without a
 * surface store. Named personas use `persona.name` instead.
 */
function audienceLabelForPersona(persona: Pick<ViewAsPreviewPersona, 'key' | 'name' | 'libraryType'>): string {
  if (persona.key === 'visitor') return __('Visitor')
  if (persona.key === 'library_member') {
    if (persona.libraryType === LibraryType.School) return __('School member')
    if (persona.libraryType === LibraryType.Classroom) return __('Classroom member')
    return __('Team member')
  }
  return persona.name ?? ''
}

function isNamedPersona(persona: Pick<ViewAsPreviewPersona, 'key'>): boolean {
  return persona.key !== 'visitor' && persona.key !== 'library_member'
}

/**
 * Splits a view-as preview banner translation into plain prefix/suffix and a bold trigger.
 * `<b>…</b>` marks the clickable phrase inside one whole-sentence translation.
 * Spaces adjoining the tags are trimmed — the banner flex row owns the gap via CSS `gap`,
 * because trailing/leading whitespace at a flex item's edge collapses in the layout box.
 */
function splitBannerCopy(translated: string): { prefix: string; trigger: string; suffix: string } {
  const match = /^(.*?)<b>(.*?)<\/b>(.*)$/.exec(translated)
  // CAT tools sometimes strip markup; keep the banner visible as one trigger.
  if (match == null) return { prefix: '', trigger: translated, suffix: '' }
  return {
    prefix: match[1].replace(/ $/, ''),
    trigger: match[2],
    suffix: match[3].replace(/^ /, ''),
  }
}

export { audienceLabelForPersona, enterPreview, exitPreview, isNamedPersona, splitBannerCopy }
export type { ViewAsPreviewPersona }
